import type moment from "moment";

import type { ImportFromTemplateResponse } from "@btnet/services-btnet/handlers/models/importFromTemplateResponse";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { type TabPermissions } from "types/enum";
import { type SplitDateTime } from "types/splitDateTime";

import { ScheduleConflictResponse } from "commonComponents/entity/scheduleConflicts/ScheduleConflict.api.types";

import { type IInactiveLineItem } from "entity/costCatalog/CostCatalog.types";

export type TemplateImportFormActions = undefined | "templateChanged" | "importStarted";

interface ITemplateImportCountsResponseData {
    scheduleItems: number;
    tasks: number;
    bidPackages: number;
    bills: number;
    linkedBills: number;
    purchaseOrders: number;
    purchaseOrdersFiles: number;
    selections: number;
    selectionsApprovedChoices: number;
    selectionsLineItemUpdates: number;
    ownerInvoices: number;
    toDos: number;
    surveys: number;
    specifications: number;
    planSets: number;
    documentFolders: number;
    documentFiles: number;
    videoFolders: number;
    videoFiles: number;
    photoFolders: number;
    estimates: number;
    canCopyLegacySelections?: boolean;
    /** Whether the source template (the job being imported from) is on Selections Reimagined. */
    isTemplateSelectionsReimagined?: boolean;
    /**
     * Whether the destination job (the job being imported into) is on Selections Reimagined. For
     * new-job creation, this reflects what a new job would resolve to.
     */
    isDestinationSelectionsReimagined?: boolean;
}

interface ITemplateImportConflictsResponseData {
    inactiveCostCodes?: { counts: IInactiveLineItem[] };
    multipleCostTypesCount: number;
    hasMultipleMarkedAs: boolean;
}

export interface ITemplateImportFormValues {
    templateId: number;
    schedule: boolean;
    tasks: boolean;
    toDos: boolean;
    selections: boolean;
    selectionsResetApproved: boolean;
    selectionsApplyPending: boolean;
    specifications: boolean;
    plans: boolean;
    documentFolders: boolean;
    documentFiles: boolean;
    photoFolders: boolean;
    videoFolders: boolean;
    videoFiles: boolean;
    surveys: boolean;
    bidPackages: boolean;
    estimates: boolean;
    estimatesRemoveExisting: boolean;
    estimatesKeepIntroClosingText: boolean;
    bills: boolean;
    linkedBills: boolean;
    purchaseOrders: boolean;
    purchaseOrdersFiles: boolean;
    ownerInvoices: boolean;
    scheduleStartDate: moment.Moment | null | undefined;
    selectionsDeadline: moment.Moment | null | undefined;
    surveyReleaseDate: moment.Moment | null | undefined;
    bidPackageDeadline: SplitDateTime | null | undefined;
    purchaseOrdersCompletion: moment.Moment | null | undefined;
    ownerInvoiceDeadline: SplitDateTime | null | undefined;
}

export class TemplateImportResponse {
    constructor(data: ImportFromTemplateResponse) {
        this.scheduleIdsAdded = data.scheduleIdsAdded ?? [];
        this.usersToAdd = (data.usersToAdd ?? []).map((item) => new BTSelectItem(item));
        this.subsToAdd = (data.subsToAdd ?? []).map((item) => new BTSelectItem(item));
        this.conflictsResponse = new ScheduleConflictResponse(data.conflicts);
        this.isCalendarOffline = data.isCalendarOffline ?? false;
        this.canViewConflicts = data.canViewConflicts ?? false;
        this.canAddUsers = data.canAddUsers ?? false;
        this.canSendNotifications = data.canSendNotifications ?? false;
        this.backgroundJobId = data.backgroundJobId ? parseInt(data.backgroundJobId) : undefined;
    }
    scheduleIdsAdded: number[];
    usersToAdd: BTSelectItem[];
    subsToAdd: BTSelectItem[];
    conflictsResponse: ScheduleConflictResponse;
    isCalendarOffline: boolean;
    canViewConflicts: boolean;
    canAddUsers: boolean;
    canSendNotifications: boolean;
    backgroundJobId?: number;
}

export class TemplateImportCountsResponse {
    constructor(data: ITemplateImportCountsResponseData) {
        this.scheduleItems = data.scheduleItems;
        this.tasks = data.tasks;
        this.bidPackages = data.bidPackages;
        this.bills = data.bills;
        this.linkedBills = data.linkedBills;
        this.purchaseOrders = data.purchaseOrders;
        this.purchaseOrdersFiles = data.purchaseOrdersFiles;
        this.selections = data.selections;
        this.selectionsApprovedChoices = data.selectionsApprovedChoices;
        this.selectionsLineItemUpdates = data.selectionsLineItemUpdates;
        this.ownerInvoices = data.ownerInvoices;
        this.toDos = data.toDos;
        this.surveys = data.surveys;
        this.specifications = data.specifications;
        this.planSets = data.planSets;
        this.documentFolders = data.documentFolders;
        this.documentFiles = data.documentFiles;
        this.videoFolders = data.videoFolders;
        this.videoFiles = data.videoFiles;
        this.photoFolders = data.photoFolders;
        this.estimates = data.estimates;
        this.canCopyLegacySelections = data.canCopyLegacySelections ?? true;
        this.isTemplateSelectionsReimagined = data.isTemplateSelectionsReimagined ?? false;
        this.isDestinationSelectionsReimagined = data.isDestinationSelectionsReimagined ?? false;
    }

    scheduleItems: number;
    tasks: number;
    bidPackages: number;
    bills: number;
    linkedBills: number;
    purchaseOrders: number;
    purchaseOrdersFiles: number;
    selections: number;
    selectionsApprovedChoices: number;
    selectionsLineItemUpdates: number;
    ownerInvoices: number;
    toDos: number;
    surveys: number;
    documentFolders: number;
    documentFiles: number;
    specifications: number;
    planSets: number;
    videoFolders: number;
    videoFiles: number;
    photoFolders: number;
    estimates: number;
    canCopyLegacySelections: boolean;
    isTemplateSelectionsReimagined: boolean;
    isDestinationSelectionsReimagined: boolean;
}

export interface ITemplateImportRequest {
    scheduleStartCopyDateUtc: moment.Moment | null;
    templateId: number;
    jobsiteId: number;
    callingPage: TabPermissions;
    importCopyOptions: ITemplateImportOptions;
    datesForImport: ITemplateImportDates;
}

interface ITemplateImportOptions {
    applyLIUpdates: boolean;
    importBidPackages: boolean;
    importDocumentFiles: boolean;
    importDocumentFolders: boolean;
    importEstimate: boolean;
    importOwnerPayments: boolean;
    importStandaloneBills: boolean;
    importLinkedBills: boolean;
    importPODocs: boolean;
    importPOs: boolean;
    importPhotoFolders: boolean;
    importSchedules: boolean;
    importSelections: boolean;
    importSpecifications: boolean;
    importPlans: boolean;
    importSurveys: boolean;
    importTasks: boolean;
    importToDos: boolean;
    importVideoFiles: boolean;
    importVideoFolders: boolean;
    resetApprovedChoices: boolean;
    resetFavoritedChoices: boolean;
    removeExistingEstimates: boolean;
    keepDestinationProposalIntroClosingText: boolean;
}

// These are all in local time, just matching key names
interface ITemplateImportDates {
    newBidPackageDeadlineUtc: moment.Moment | null;
    newPOsStartDateUtc: moment.Moment | null;
    newSelectionsStartDateUtc: moment.Moment | null;
    newOwnerPaymentDeadlineLocal: moment.Moment | null;
    newSurveyReleaseDateLocal: moment.Moment | null;
}

export interface ITemplateImportConflictsRequest {
    jobId: number;
    copyOptions: CopyOptions[];
    isExistingJob: boolean;
}

export class TemplateImportConflictsResponse {
    constructor(data: ITemplateImportConflictsResponseData) {
        this.inactiveCostCodes = data.inactiveCostCodes ? data.inactiveCostCodes.counts : [];
        this.multipleCostTypesCount = data.multipleCostTypesCount;
        this.hasMultipleMarkedAs = data.hasMultipleMarkedAs;
    }
    inactiveCostCodes: IInactiveLineItem[];
    multipleCostTypesCount: number;
    hasMultipleMarkedAs: boolean;
}

export enum CopyOptions {
    None = 0,
    Tasks = 1,
    Selections = Tasks << 1,
    DocumentFolders = Selections << 1,
    PhotoFolders = DocumentFolders << 1,
    PurchaseOrders = PhotoFolders << 1,
    Estimates = PurchaseOrders << 1,
    BidPackages = Estimates << 1,
    ToDos = BidPackages << 1,
    Bills = ToDos << 1,
    CustomerInvoices = Bills << 1,
    DocumentFiles = CustomerInvoices << 1,
    ResetApprovedSelections = DocumentFiles << 1,
    VideoFolders = ResetApprovedSelections << 1,
    VideoFiles = VideoFolders << 1,
    ResetFavoritedChoices = VideoFiles << 1,
    Surveys = ResetFavoritedChoices << 1,
    PhotoFiles = Surveys << 1,
    PurchaseOrderFiles = PhotoFiles << 1,
    ApplyPendingLineItemUpdates = PurchaseOrderFiles << 1,
    ScheduleItems = ApplyPendingLineItemUpdates << 1,
    ScheduleItemAttachments = ScheduleItems << 1,
    All = Tasks |
        Selections |
        DocumentFolders |
        PhotoFolders |
        PurchaseOrders |
        Estimates |
        BidPackages |
        ToDos |
        Bills |
        CustomerInvoices |
        DocumentFiles |
        ResetApprovedSelections |
        VideoFolders |
        VideoFiles |
        ResetFavoritedChoices |
        Surveys |
        PhotoFiles |
        PurchaseOrderFiles |
        ApplyPendingLineItemUpdates |
        ScheduleItems |
        ScheduleItemAttachments,
}
